import { Injectable } from "@angular/core";

/**
 * Service for date formatting in Italian format
 * Provides methods to obtain dates formatted according to various formats
 */
@Injectable({
  providedIn: "root",
})
export class DateFormatterService {
  /** Array of month names in Italian */
  private readonly monthNames: string[] = [
    "Gennaio",
    "Febbraio",
    "Marzo",
    "Aprile",
    "Maggio",
    "Giugno",
    "Luglio",
    "Agosto",
    "Settembre",
    "Ottobre",
    "Novembre",
    "Dicembre",
  ];

  /** Array of weekday names in Italian (starting from Sunday) */
  private readonly dayNames: string[] = [
    "Domenica",
    "Lunedì",
    "Martedì",
    "Mercoledì",
    "Giovedì",
    "Venerdì",
    "Sabato",
  ];

  /** Mapping for translating English weekday names (from DB) to Italian */
  private readonly dayNameTranslationMap: Record<string, string> = {
    sunday: "Domenica",
    monday: "Lunedì",
    tuesday: "Martedì",
    wednesday: "Mercoledì",
    thursday: "Giovedì",
    friday: "Venerdì",
    saturday: "Sabato",
  };

  /**
   * Formats a date in "DD Month YYYY" format (e.g. "20 Maggio 2025")
   * @param date The date to format (Date object, ISO string or timestamp)
   * @returns String formatted in "DD Month YYYY" format
   */
  public formatFullDate(date: Date | string | number = new Date()): string {
    const dateObj = typeof date === "object" ? date : new Date(date);
    const day = dateObj.getDate();
    const month = this.monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  }

  /**
   * Formats a date in "Weekday DD Month YYYY" format (e.g. "Sabato 31 Maggio 2025")
   */
  public formatLongDate(date: Date | string | number = new Date()): string {
    const dateObj = this._toDate(date);
    const weekday = this.dayNames[dateObj.getDay()];
    const day = dateObj.getDate();
    const month = this.monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    return `${weekday} ${day} ${month} ${year}`;
  }

  /**
   * Gets the current date formatted in "DD Month YYYY" format
   * @returns Current date formatted (e.g. "20 Maggio 2025")
   */
  public getCurrentFullDate(): string {
    return this.formatFullDate(new Date());
  }

  /**
   * Gets only the month name from a date
   * @param date The date to extract the month from (Date object, ISO string or timestamp)
   * @returns The month name in Italian (e.g. "Maggio")
   */
  public getMonthName(date: Date | string | number = new Date()): string {
    const dateObj = this._toDate(date);
    const formatter = new Intl.DateTimeFormat("it-IT", {
      month: "long",
      timeZone: "Europe/Rome",
    });
    const month = formatter.format(dateObj);
    return month.charAt(0).toUpperCase() + month.slice(1);
  }

  /**
   * Gets only the day number from a date
   * @param date The date to extract the day from (Date object, ISO string or timestamp)
   * @returns The day number (e.g. 20)
   */
  public getDayNumber(date: Date | string | number = new Date()): number {
    const dateObj = this._toDate(date);
    const formatter = new Intl.DateTimeFormat("it-IT", {
      day: "numeric",
      timeZone: "Europe/Rome",
    });
    return parseInt(formatter.format(dateObj));
  }

  /**
   * Translates a weekday name from English to Italian (e.g. "Tuesday" → "Martedì")
   */
  public translateWeekday(englishDay: string): string {
    return this.dayNameTranslationMap[englishDay] || englishDay;
  }

  /**
   * Utility to convert input to a Date object safely
   */
  private _toDate(date: Date | string | number): Date {
    return date instanceof Date ? date : new Date(date);
  }

  /**
   * Gets only the hours and minutes from a date in "HH:mm" format
   * @param date The date to extract the time from (Date object, ISO string or timestamp)
   * @returns The time in "HH:mm" format (e.g. "09:30")
   */
  public getTimeHHmm(date: Date | string | number = new Date()): string {
    const dateObj = this._toDate(date);
    const hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  /**
   * Returns the data in the format "DD/MM/YYYY".
   * @param date the date object.
   * @return the formatted date string.
   */
  public getSlashedDate(date: Date | string | number = new Date()): string {
    const dateObj = this._toDate(date);
    const day = dateObj.getDate();
    const month = this.monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Composes the date to obtain the format "DD/MM/YYYY - HH:mm"
   * @param date the date object.
   * @returns the formatted date string.
   */
  public getFullSlashedDateWithTime(date: Date | string | number = new Date()): string {
    const dateObj = this._toDate(date);
    const day = dateObj.getDate().toString().padStart(2, "0");
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const year = dateObj.getFullYear();
    const time = this.getTimeHHmm(date);
    return `${day}/${month}/${year} - ${time}`;
  }
}